import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { api, errMsg } from "../lib/api";
import { TauMark } from "../components/app/Layout";

export default function PlanQR() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [token, setToken] = useState(null);
  const [err, setErr] = useState("");
  const enable = useCallback(() => api.post(`/plans/${id}/qr-token`).then((r) => setToken(r.data.token)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { api.get(`/plans/${id}`).then((r) => setP(r.data)).catch((e) => setErr(errMsg(e))); enable(); }, [id, enable]);
  const rotate = async () => {
    try { const r = await api.post(`/plans/${id}/qr-token`, null, { params: { rotate: true } }); setToken(r.data.token); toast.success("New code created. Old printouts no longer work."); } catch (e) { toast.error(errMsg(e)); }
  };
  if (err) return <div className="p-8 text-sm text-stone-700" data-testid="qr-error">{err} <Link to={`/plans/${id}`} className="underline">Back to plan</Link></div>;
  if (!p || !token) return null;
  const url = `${window.location.origin}/rate/${token}`;
  return (
    <div className="min-h-screen bg-white">
      <div className="flex flex-wrap gap-2 border-b p-4 print:hidden">
        <Link to={`/plans/${id}`}><Button variant="outline" data-testid="qr-back-button">Back to plan</Button></Link>
        <Button onClick={() => window.print()} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="qr-print-button"><Printer className="h-4 w-4 mr-2" />Print</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="ghost" data-testid="qr-rotate-button"><RefreshCw className="h-4 w-4 mr-2" />Replace code</Button></AlertDialogTrigger>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader><AlertDialogTitle>Replace this QR code?</AlertDialogTitle><AlertDialogDescription>All posters already printed will stop working. Use this only if the code was shared where it should not be.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={rotate} data-testid="qr-rotate-confirm">Replace</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <div className="mx-auto flex max-w-2xl flex-col items-center px-8 py-12 text-center" data-testid="qr-poster">
        <TauMark className="h-10 w-10 text-amber-900" />
        <p className="mt-2 text-xs uppercase tracking-[0.25em] font-semibold text-amber-800">Saint Francis of Assisi Parish · Brgy. Mabuhay</p>
        <h1 className="mt-6 font-serif-display text-4xl font-bold text-amber-950" data-testid="qr-program-title">{p.intervention}</h1>
        <p className="mt-2 text-stone-600">{p.gkk_name}{p.location ? ` · ${p.location}` : ""}</p>
        <div className="my-10 rounded-2xl border-4 border-amber-900 p-6"><QRCodeSVG value={url} size={300} fgColor="#451A03" data-testid="qr-code" /></div>
        <p className="font-serif-display text-2xl font-semibold text-amber-950">Did this program help you?</p>
        <p className="mt-1 text-stone-700">Nakatabang ba kini nga programa kanimo? · Nakatulong ba sa iyo ang programang ito?</p>
        <p className="mt-4 text-sm text-stone-600">Scan with your phone camera. Voluntary and anonymous — no sign-in, no names.</p>
        <p className="mt-6 break-all font-mono-data text-xs text-stone-400" data-testid="qr-url">{url}</p>
      </div>
    </div>
  );
}
